import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Use CDN-hosted worker for Rspack compatibility
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PDFViewerProps {
    url: string
    onAllPagesViewed: () => void
    disableDownload?: boolean
}

const PDFViewer: React.FC<PDFViewerProps> = ({
    url,
    onAllPagesViewed,
    disableDownload = true,
}) => {
    const [numPages, setNumPages] = useState<number>(0)
    const [viewedPages, setViewedPages] = useState<Set<number>>(new Set())
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [hasReachedBottom, setHasReachedBottom] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
    const observerRef = useRef<IntersectionObserver | null>(null)

    // Memoize options to prevent unnecessary reloads
    const documentOptions = useMemo(
        () => ({
            cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
            cMapPacked: true,
        }),
        []
    )

    // Memoize page width
    const pageWidth = useMemo(() => Math.min(800, window.innerWidth - 48), [])

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages)
        setIsLoading(false)
        setError(null)
    }

    const onDocumentLoadError = () => {
        setError('Không thể tải file PDF. Vui lòng thử lại.')
        setIsLoading(false)
    }

    const setPageRef = useCallback(
        (pageNum: number, ref: HTMLDivElement | null) => {
            if (ref) {
                pageRefs.current.set(pageNum, ref)
                if (observerRef.current) {
                    observerRef.current.observe(ref)
                }
            }
        },
        []
    )

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const pageNumber = parseInt(
                            entry.target.getAttribute('data-page') || '0'
                        )
                        if (pageNumber > 0) {
                            setViewedPages((prev) => {
                                const newSet = new Set(prev)
                                newSet.add(pageNumber)
                                return newSet
                            })
                        }
                    }
                })
            },
            {
                root: containerRef.current,
                threshold: 0.3, // 30% visible is enough to count as viewed
            }
        )

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    // Only enable when scrolled to absolute bottom of document
    useEffect(() => {
        const container = containerRef.current
        if (!container || numPages === 0 || hasReachedBottom) return

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container
            // Must scroll to within 20px of absolute bottom
            const isAtAbsoluteBottom =
                scrollTop + clientHeight >= scrollHeight - 20

            if (isAtAbsoluteBottom && !hasReachedBottom) {
                setHasReachedBottom(true)
                onAllPagesViewed()
            }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [numPages, onAllPagesViewed, hasReachedBottom])

    const handleContextMenu = (e: React.MouseEvent) => {
        if (disableDownload) {
            e.preventDefault()
        }
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                    onClick={() => {
                        setError(null)
                        setIsLoading(true)
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Thử lại
                </button>
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            className="h-full overflow-y-auto bg-gray-100"
            onContextMenu={handleContextMenu}
            style={{ userSelect: 'none' }}
        >
            {isLoading && (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
            )}

            <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={null}
                options={documentOptions}
            >
                {Array.from(new Array(numPages), (_, index) => (
                    <div
                        key={`page_${index + 1}`}
                        ref={(ref) => setPageRef(index + 1, ref)}
                        data-page={index + 1}
                        className="mb-4 flex justify-center"
                    >
                        <Page
                            pageNumber={index + 1}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />
                    </div>
                ))}
            </Document>

            {numPages > 0 && (
                <div className="sticky bottom-4 left-0 right-0 flex justify-center pointer-events-none">
                    <div className="bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                        {hasReachedBottom
                            ? `✓ Đã đọc xong ${numPages} trang`
                            : `Trang ${Math.max(1, viewedPages.size)}/${numPages} - Kéo xuống cuối để tiếp tục`}
                    </div>
                </div>
            )}
        </div>
    )
}

export default PDFViewer
